import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError, BACKEND } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import FileUploader from "../../components/admin/FileUploader";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { SortableList, SortableItem, DragHandle } from "../../components/admin/SortableList";

const empty = {
  title: "", subtitle: "", media_url: "", media_type: "image",
  link_url: "", cta_label: "", is_active: true,
};

export default function Sliders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/admin/sliders"); setItems(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.media_url) { toast.error("Please upload media first"); return; }
    try {
      if (editing) await api.put(`/admin/sliders/${editing.id}`, form);
      else await api.post("/admin/sliders", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete slider?")) return;
    try { await api.delete(`/admin/sliders/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Sliders"
        subtitle="Homepage hero slides (image or video)."
        actions={
          <Button onClick={openNew} data-testid="add-slider-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New slide
          </Button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sliders-grid">
          {items.length === 0 && <div className="col-span-full text-center py-12 text-[#4B5563]">No slides yet.</div>}
          <SortableList items={items} setItems={setItems} reorderEndpoint="/admin/reorder/sliders" layout="grid">
            {items.map((it) => (
              <SortableItem key={it.id} id={it.id} as="div" className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`slider-card-${it.id}`}>
                {(handleProps) => (
                  <>
                    <div className="relative aspect-video bg-[#F7F8F6] overflow-hidden">
                      <div className="absolute top-2 left-2 z-10 bg-white/90 rounded p-1 shadow"><DragHandle handleProps={handleProps} /></div>
                      {it.media_type === "youtube" && it.media_url ? (
                        <div className="w-full h-full flex items-center justify-center text-xs text-white/80 bg-gradient-to-br from-[#C2410C] to-[#0F172A] px-4 text-center">
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase opacity-70">YouTube</div>
                            <div className="mt-1 truncate max-w-[240px]">{it.media_url}</div>
                          </div>
                        </div>
                      ) : it.media_type === "video" ? (
                        <video src={`${BACKEND}${it.media_url}`} controls className="w-full h-full object-cover" />
                      ) : it.media_url ? (
                        <img src={`${BACKEND}${it.media_url}`} alt={it.title || ""} className="w-full h-full object-cover" />
                      ) : null}
                    </div>
                    <div className="p-4">
                      <div className="font-medium text-[#1F2937]">{it.title || "Untitled"}</div>
                      <div className="text-xs text-[#4B5563] truncate">{it.subtitle || "—"}</div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"}`}>
                          {it.is_active ? "Live" : "Hidden"}
                        </span>
                        <div>
                          <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                          <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </SortableItem>
            ))}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="slider-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit slide" : "New slide"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="slider-title" /></div>
            <div className="space-y-2"><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="slider-subtitle" /></div>
            <div className="space-y-2">
              <Label>Media type</Label>
              <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v, media_url: "" })}>
                <SelectTrigger data-testid="slider-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="video">Video (upload)</SelectItem>
                  <SelectItem value="youtube">YouTube video</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.media_type === "youtube" ? (
              <div className="space-y-2">
                <Label>YouTube URL</Label>
                <Input
                  value={form.media_url}
                  onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                  placeholder="https://youtu.be/dQw4w9WgXcQ or https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                  data-testid="slider-youtube-url"
                />
                <p className="text-xs text-[#4B5563]">Paste any YouTube share link — the site will auto-play muted on the homepage hero.</p>
              </div>
            ) : (
              <FileUploader
                kind={form.media_type === "video" ? "video" : "image"}
                accept={form.media_type === "video" ? "video/*" : "image/*"}
                label="Media file" currentUrl={form.media_url}
                onUploaded={(url) => setForm({ ...form, media_url: url })}
                testId="upload-slide"
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>CTA label</Label><Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="slider-cta" /></div>
              <div className="space-y-2"><Label>CTA link</Label><Input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} data-testid="slider-link" /></div>
            </div>
            <p className="text-xs text-[#4B5563]">Order: drag the card on the slider list to reorder. New slides are added at the top.</p>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="slider-active" />
              <Label>Live</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-slider" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
